import {
  APAutofills,
  APAutofillswPages,
  PeopleResponse,
  PersonaWithRolesResponse,
  PersonRoleHistoryResponse,
} from "types/Models/apAutofills";
import { emptyApi } from "./emptyApi";
import qs from "qs";
import { DirectorAutofill } from "types/Models/directorAutofill";

export type RoleBlockPayload =
  | {
      role_type: "DIRECTOR";
      appointed_on?: string | null;
      resigned_on?: string | null;
      ap_status?: "ACTIVE" | "FORMER" | null;
      is_authorised_signatory?: boolean;
    }
  | {
      role_type: "OFFICER";
      role_id?: string | null;
      designation?: string | null;
      appointed_on?: string | null;
      resigned_on?: string | null;
      ap_status?: "ACTIVE" | "FORMER" | null;
      is_authorised_signatory?: boolean;
    }
  | {
      role_type: "SHAREHOLDER";
      ownership_stake: string;
      country_id?: string | null;
      tin?: string | null;
      issued_common_stock?: number | null;
      issued_preferred_stock?: number | null;
    };

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAPAutofills: build.query<
      APAutofillswPages,
      {
        entityId: string;
        personaType: string;
        searchTerm?: string;
        isSort: boolean;
        pageNum?: number;
      }
    >({
      query: ({ entityId, personaType, searchTerm, isSort, pageNum }) => {
        let queryUrl = qs.stringify(
          {
            page_num: pageNum,
            search_term: searchTerm,
            sort_by_role: isSort,
            persona_type: personaType,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/autofill/entity/${entityId}/personas/${queryUrl}`,
        };
      },
      providesTags: (result) => (result ? [{ type: "AP_AUTOFILLS" }] : []),
    }),

    getPersonaTypes: build.query<
      {
        name: string;
        uuid: string;
      }[],
      {
        entityId: string;
      }
    >({
      query: ({ entityId }) => {
        return {
          url: `/api/inkle/autofill/entity/${entityId}/persona-types/`,
        };
      },
    }),

    addAPAutoFill: build.mutation<
      APAutofillswPages & DirectorAutofill,
      {
        personaType: string;
        entityId: string;
        payload: any;
      }
    >({
      query: ({ personaType, entityId, payload }) => {
        let queryUrl = qs.stringify(
          {
            persona_type: personaType,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/autofill/entity/${entityId}/personas/${queryUrl}`,
          method: "post",
          body: payload,
        };
      },
      invalidatesTags: (result) =>
        result
          ? ["AP_AUTOFILLS", "AUTOFILL", "PEOPLE", "PERSONA_ROLE_HISTORY"]
          : [],
    }),

    updateAPAutoFill: build.mutation<
      APAutofills & DirectorAutofill,
      {
        personaType: string;
        entityId: string;
        personaId: string;
        payload: any;
      }
    >({
      query: ({ personaType, entityId, personaId, payload }) => {
        let queryUrl = qs.stringify(
          {
            persona_type: personaType,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/autofill/entity/${entityId}/persona/${personaId}/${queryUrl}`,
          method: "put",
          body: payload,
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: "AP_AUTOFILLS" },
              { type: "AUTOFILL" },
              { type: "PEOPLE" },
              { type: "PERSONA_ROLE_HISTORY" },
            ]
          : [],
    }),
    deleteAPAutoFill: build.mutation<
      APAutofillswPages & DirectorAutofill,
      {
        entityId: string;
        personaId: string;
      }
    >({
      query: ({ entityId, personaId }) => {
        return {
          url: `/api/inkle/autofill/entity/${entityId}/persona/${personaId}/`,
          method: "DELETE",
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: "AP_AUTOFILLS" },
              { type: "AUTOFILL" },
              { type: "PEOPLE" },
              { type: "PERSONA_ROLE_HISTORY" },
            ]
          : [],
    }),
    getPeopleForEntity: build.query<
      PeopleResponse,
      {
        entityId: string;
      }
    >({
      query: ({ entityId }) => {
        return {
          url: `/api/inkle/autofill/entity/${entityId}/people/`,
        };
      },
      providesTags: ["PEOPLE"],
    }),
    createPersonaWithRoles: build.mutation<
      PersonaWithRolesResponse,
      {
        groupId: string;
        entityId: string;
        payload: {
          persona_autofill_id?: string | null;
          first_name?: string;
          last_name?: string;
          email?: string;
          address_id?: string | null;
          roles: RoleBlockPayload[];
        };
      }
    >({
      query: ({ groupId, entityId, payload }) => {
        return {
          url: `/api/inkle/autofill/group/${groupId}/entity/${entityId}/people/`,
          method: "post",
          body: payload,
        };
      },
      invalidatesTags: [
        "PEOPLE",
        "AP_AUTOFILLS",
        "AUTOFILL",
        "SHAREHOLDERS",
        "AP_DETAILS",
        "PERSONA_ROLE_HISTORY",
      ],
    }),
    getPersonaRoleHistory: build.query<
      PersonRoleHistoryResponse,
      {
        entityId: string;
        personaAutofillId: string;
        pageNum?: number;
      }
    >({
      query: ({ entityId, personaAutofillId, pageNum }) => {
        let queryUrl = qs.stringify(
          { page_num: pageNum },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/autofill/entity/${entityId}/persona-autofill/${personaAutofillId}/history/${queryUrl}`,
        };
      },
      providesTags: ["PERSONA_ROLE_HISTORY"],
    }),
    getEntityAPDetails: build.query<
      {
        director_exists: boolean | null;
        officer_exists: boolean | null;
        shareholder_exists: boolean | null;
      },
      {
        entityId: string;
      }
    >({
      query: ({ entityId }) => {
        return {
          url: `/api/inkle/autofill/entity/${entityId}/authorised_persona_details/`,
        };
      },
      providesTags: ["AP_DETAILS"],
    }),
    updateEntityAPDetails: build.mutation<
      {
        director_exists: boolean | null;
        officer_exists: boolean | null;
        shareholder_exists: boolean | null;
      },
      {
        entityId: string;
        payload: {
          director_exists: boolean | null;
          officer_exists: boolean | null;
          shareholder_exists: boolean | null;
        };
      }
    >({
      query: ({ entityId, payload }) => {
        return {
          url: `/api/inkle/autofill/entity/${entityId}/authorised_persona_exist/`,
          method: "put",
          body: payload,
        };
      },
      async onQueryStarted(patch, { dispatch, queryFulfilled }) {
        try {
          dispatch(
            extendedApi.util.updateQueryData(
              "getEntityAPDetails",
              {
                entityId: patch.entityId,
              },
              (draft) => {
                draft.director_exists = patch.payload.director_exists;
                draft.officer_exists = patch.payload.officer_exists;
                draft.shareholder_exists = patch.payload.shareholder_exists;
              }
            )
          );
        } catch {}
      },
      invalidatesTags: ["AP_DETAILS"],
    }),
  }),
});

export const {
  useGetAPAutofillsQuery,
  useGetPersonaTypesQuery,
  useAddAPAutoFillMutation,
  useUpdateAPAutoFillMutation,
  useDeleteAPAutoFillMutation,
  useGetEntityAPDetailsQuery,
  useUpdateEntityAPDetailsMutation,
  useGetPeopleForEntityQuery,
  useCreatePersonaWithRolesMutation,
  useGetPersonaRoleHistoryQuery,
} = extendedApi;
